import type { StepTimelineDto, TimelineEntryDto } from '@bk/contracts';
import { useEffect, useState } from 'react';

/** Minuti e secondi: «4:06». */
export const clock = (s: number) =>
  `${Math.floor(s / 60)}:${String(Math.max(0, Math.round(s)) % 60).padStart(2, '0')}`;

/** L'ora che scorre, un secondo alla volta, finché serve. */
export function useNow(on = true): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on]);
  return now;
}

const ms = (iso: string | null) => (iso ? new Date(iso).getTime() : null);
/** Quanto è durato (o dura) un tratto, in secondi. */
const spent = (a: string | null, b: string | null, now: number) => {
  const s = ms(a);
  return s === null ? 0 : ((ms(b) ?? now) - s) / 1000;
};

/**
 * Il tempo di un passo per la barra dei passi (ADR 0167): la durata per i passi fatti e per quello aperto, «si prepara»
 * per un passo che viene dopo e lavora già. Un passo che viene dopo e non lavora non ha tempo: i lavori comuni (lettura
 * dei documenti, ricerca online) non sono il suo progetto.
 */
export function StepTime({ t, at }: { t: StepTimelineDto | null | undefined; at: 'prima' | 'qui' | 'dopo' }) {
  const now = useNow(!!t && !t.fine && at === 'qui');
  if (!t) return null;
  if (at === 'dopo') return t.fine ? null : <span className="bkc-time"> · si prepara</span>;
  return <span className="bkc-time"> · {clock(spent(t.inizio, t.fine, now))}</span>;
}

function Icon({ e }: { e: TimelineEntryDto['esito'] }) {
  if (e === 'fatto')
    return <span className="mt-0.5 w-4 shrink-0 text-center text-emerald-600">✓</span>;
  if (e === 'errore')
    return <span className="mt-0.5 w-4 shrink-0 text-center text-red-600">!</span>;
  if (e === 'in_corso')
    return (
      <span className="mt-1 h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-accent border-t-transparent" />
    );
  return (
    <span className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full border-2 border-dashed border-ink/25" />
  );
}

function Row({ v, now, sub }: { v: TimelineEntryDto; now: number; sub?: boolean }) {
  const s = spent(v.inizio, v.fine, now);
  const time =
    v.esito === 'previsto' ? (v.previsto_s ? `~${clock(v.previsto_s)}` : 'in attesa') : clock(s);
  const pct =
    v.esito === 'in_corso' && v.previsto_s ? Math.min(100, (s / v.previsto_s) * 100) : null;
  return (
    <li className={`flex items-start gap-2 border-t border-black/5 py-2 ${sub ? 'pl-6' : ''}`}>
      <Icon e={v.esito} />
      <div className="min-w-0 flex-1">
        <span
          className={
            v.esito === 'previsto'
              ? 'text-ink/45'
              : v.esito === 'fatto'
                ? 'text-ink/60'
                : 'text-ink/90'
          }
        >
          {v.etichetta}
        </span>
        {v.esito === 'in_corso' && (
          <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-black/5">
            <div
              className={`h-full rounded-full bg-accent ${pct === null ? 'w-1/3 animate-pulse' : ''}`}
              style={pct === null ? undefined : { width: `${pct}%` }}
            />
          </div>
        )}
      </div>
      <span
        className={`shrink-0 font-mono text-xs tabular-nums ${v.esito === 'fatto' ? 'text-emerald-700' : v.esito === 'errore' ? 'text-red-700' : v.esito === 'previsto' ? 'text-ink/40' : 'text-ink/70'}`}
      >
        {time}
      </span>
    </li>
  );
}

/**
 * Il lettore e il contatore del passo (contratto n. 1, art. 11.2-bis; ADR 0167): ogni attività del consulente con il suo
 * stato e il suo tempo, le attese comprese, e in alto il totale accanto al tempo massimo del contratto.
 */
export function StepTimeline({ t }: { t: StepTimelineDto }) {
  const now = useNow(!t.fine);
  const total = spent(t.inizio, t.fine, now);
  const over = total > t.massimo_s;
  const top = t.voci.filter((v) => !v.padre || !t.voci.some((x) => x.id === v.padre));
  return (
    <div
      className="rounded-xl border border-accent/20 bg-accent/5 px-4 py-3 text-sm"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="font-medium text-ink/80">{t.fine ? 'Fatto' : 'Al lavoro'}</span>
        <span className="text-xs text-ink/55">
          <span
            className={`font-mono text-sm tabular-nums ${over ? 'text-red-700' : 'text-ink/90'}`}
          >
            {clock(total)}
          </span>{' '}
          · tempo massimo {clock(t.massimo_s)}
        </span>
      </div>
      <div className="mb-1 mt-2 h-1.5 overflow-hidden rounded-full bg-black/5">
        <div
          className={`h-full rounded-full ${over ? 'bg-red-500' : 'bg-accent'}`}
          style={{ width: `${Math.min(100, (total / t.massimo_s) * 100)}%` }}
        />
      </div>
      <ul>
        {top.map((v) => (
          <li key={v.id} className="list-none">
            <ul>
              <Row v={v} now={now} />
              {t.voci
                .filter((x) => x.padre === v.id)
                .map((x) => (
                  <Row key={x.id} v={x} now={now} sub />
                ))}
            </ul>
          </li>
        ))}
      </ul>
      <p className="mt-1 text-xs text-ink/45">
        {over ? 'Oltre il tempo massimo: lo segno per migliorarlo. ' : ''}Puoi lasciare la pagina,
        il lavoro continua.
      </p>
    </div>
  );
}
